import {
  ComparatorContext,
  getRendererFromDescriptor,
  useField,
  usePage,
} from '@kizi/comparator-core';
import { Div } from '@kizi/core-components';
import React, { useContext, useEffect, useState } from 'react';
import { isMobile, useHandleSubmitClick } from '../../utils/global';
import {
  ProductNewProps,
  ProductsNewProps,
  ProductsNewWrapperProps,
} from '../ProductNew';

import { TopThreeBadge } from './TopThreeBadge';
import useTabTracking from '../../utils/hooks';
import { SwipeComponent } from '../SwipeComponent';

export interface TopThreeProductProps extends ProductNewProps {
  moreInfoModal?: string;
  data: any;
  isOpen: boolean;
  setIsOpen: any;
  expandedPartStartIndex: any;
  setExpandedPartStartIndex: any;
  expandedPartSelectedIndex: any;
  setExpandedPartSelectedIndex: any;
  coveragesBlockMinHeight?: number | null;
  setCoverageSectionMaxHeight: React.Dispatch<React.SetStateAction<number>>;
  coverageSectionMaxHeight: number;
  hidePriceSymbol?: boolean;
  customTopThreeCard?: string;
}

export const TopThreeProduct: React.FC<TopThreeProductProps> = ({
  data,
  priceMode,
  promotionModals,
  nextIndex,
  productTypes,
  multiCompare,
  comparatorName,
  isOpen,
  setIsOpen,
  setExpandedPartStartIndex,
  setExpandedPartSelectedIndex,
  customTopThreeCard,
  registerAnalyticsAddToCartEvent
}): any => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { state } = usePage();

  if (!data) {
    return null;
  }

  const {
    affiliateUrl,
    purchasableProduct,
    provider,
    orderType,
    type,
    productType,
    incentive,
  } = data;

  const hasPromotionBar =
    orderType !== 'NOT_ORDERABLE' &&
    (orderType !== 'AFFILIATE' ||
      (orderType === 'AFFILIATE' && comparatorName === 'os-health')) &&
    purchasableProduct;

  const shouldOpenNewTab =
    affiliateUrl &&
    ['home', 'car', 'travel', 'health', 'os-triple-play'].includes(type);

  let category = type;
  if (category === 'os-triple-play') category = 'TriplePlay';
  if (category.includes('health')) category = 'HealthCare';

  const { name: providerName, id: providerId } = provider;
  const shouldOpenApplyModal =
    (type === 'home' &&
      (providerName === 'ABN AMRO' ||
        providerId === 'krn:kizi:product-supplier:ccfe7b49')) ||
    (comparatorName === 'os-health' &&
      orderType === 'AFFILIATE' &&
      incentive !== null);

  const handleSubmit = useHandleSubmitClick(
    shouldOpenNewTab,
    data,
    nextIndex,
    registerAnalyticsAddToCartEvent,
  );

  const openTab = (index: number): void => {
    if (isOpen) {
      setExpandedPartSelectedIndex(index);
    } else {
      setIsOpen(true);
      setExpandedPartStartIndex(index);
    }
  };

  const TopThreeHealthBadgeComponent = (
    <TopThreeBadge type={type} tag={data?.tags} />
  );

  if (customTopThreeCard) {
    return getRendererFromDescriptor({
      type: customTopThreeCard,
      props: {
        data,
        productType,
        openTab: (index) => openTab(index),
        promotionModals,
        hasPromotionBar,
        productTypes,
        multiCompare,
        isOpen,
        priceMode,
        registerAnalyticsAddToCartEvent,
        nextIndex,
        shouldOpenApplyModal,
        onCompareChange: (id) =>
          (state?.onCompareChange as Function)(null, id, 'top3'),
        setShowConfirmationModal,
        showConfirmationModal,
        handleSubmit,
        TopThreeHealthBadgeComponent,
      },
    });
  }
};

const order = [
  'lowestPrice',
  'highestScore',
  'highestScoreForPrice',
  'fastestSpeed',
  'featured',
];
const compareProducts = (a, b) => {
  const indexA = order.indexOf(a?.tags);
  const indexB = order.indexOf(b?.tags);

  if (indexA === -1) return 1;
  if (indexB === -1) return -1;

  return indexA - indexB;
};

export const TopThreeProducts: React.FC<
  ProductsNewProps & {
    wrapperStyles?: React.CSSProperties;
    swiperThresholdValues?: number[];
  }
> = ({
  priceMode = 'month',
  productList = [],
  promotionBarModals,
  productTypes,
  tariffsTab,
  comparatorName,
  wrapperStyles,
  swiperThresholdValues,
  registerAnalyticsAddToCartEvent,
  ...restProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedPartStartIndex, setExpandedPartStartIndex] = useState(-1);
  const [expandedPartSelectedIndex, setExpandedPartSelectedIndex] =
    useState(-1);
  const { trackTabClick } = useTabTracking();

  const [coverageSectionMaxHeight, setCoverageSectionMaxHeight] = useState(0);

  useEffect(() => {
    if (isOpen) {
      trackTabClick(expandedPartSelectedIndex, 'top-3');
    }
  }, [isOpen, expandedPartSelectedIndex]);

  const productListComponents = productList
    .sort((a, b) => {
      if (comparatorName === 'os-triple-play') {
        return compareProducts(a, b);
      }
      return 0;
    })
    .map((p) => {
      const _props: ProductNewProps = {
        data: p,
        comparatorName,
        ...restProps,
        priceMode,
        productTypes,
      };

      const TariffsTab: React.ReactNode[] = [];
      if (tariffsTab) {
        TariffsTab.push(
          getRendererFromDescriptor({
            type: tariffsTab,
            props: {
              data: p,
              priceMode: priceMode,
            },
          }),
        );
      }

      return (
        <TopThreeProduct
          key={'product-' + p?.id}
          promotionModals={promotionBarModals}
          tariffsTab={TariffsTab}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          expandedPartStartIndex={expandedPartStartIndex}
          setExpandedPartStartIndex={setExpandedPartStartIndex}
          expandedPartSelectedIndex={expandedPartSelectedIndex}
          setExpandedPartSelectedIndex={setExpandedPartSelectedIndex}
          setCoverageSectionMaxHeight={setCoverageSectionMaxHeight}
          coverageSectionMaxHeight={coverageSectionMaxHeight}
          registerAnalyticsAddToCartEvent={registerAnalyticsAddToCartEvent}
          {..._props}
        />
      );
    });

  return (
    <>
      {isMobile() ? (
        <SwipeComponent swiperThresholdValues={swiperThresholdValues}>
          {productListComponents}
        </SwipeComponent>
      ) : (
        <Div
          variant="topThreeDesktopWrapper"
          css={{ ...(wrapperStyles || {}) }}
        >
          {productListComponents}
        </Div>
      )}
    </>
  );
};

export const TopThreeProductsWrapper: React.FC<ProductsNewWrapperProps> = ({
  filters,
  defaultFilters,
  tariffsTab,
  wrapperStyles,
  registerAnalyticsAddToCartEvent,
  ...restProps
}) => {
  const { name } = useContext(ComparatorContext);
  const { state } = usePage();
  const filtersField = useField(filters);

  if (
    !filtersField ||
    !Object.prototype.hasOwnProperty.call(filtersField, 'value')
  ) {
    return null;
  }

  const _filters = JSON.parse(filtersField.value as string);

  if (!_filters) return null;
  return (
    <TopThreeProducts
      {...restProps}
      {..._filters}
      defaultFilters={defaultFilters}
      productList={state?.topThree as any[]}
      priceMode={_filters.showPriceMode?.[0] || 'month'}
      tariffsTab={tariffsTab}
      comparatorName={name}
      css={{
        maxWidth: ['280px', '280px', 'initial', 'initial'],
      }}
      wrapperStyles={wrapperStyles}
      registerAnalyticsAddToCartEvent={registerAnalyticsAddToCartEvent}
    />
  );
};

export default TopThreeProductsWrapper;
